import usePairedDevice from "@/hooks/usePairedDevice";
import {
  getStoredValue,
  postEvent,
  setStoredValue,
  useConfigContext,
  useDialogContext,
  useDrawerContext,
  useGuardedUpdater,
  useNativeMessagingContext,
  useSnackbarContext,
} from "@WahooFitness/wahoo-offline-mfe";
import {
  BlueSwitch,
  ChevronRightIcon,
  FancyIcon,
  MenuList,
  MenuListItemType,
  MenuListItemVariant,
  WfIconMiscBikeBellRegular,
} from "@WahooFitness/redesignr";
import {
  BacklightCfg,
  PageColorMode,
  WSMBoltAppPasscodeState,
} from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import {
  Asleep,
  Chat,
  Checkmark,
  CircleFill,
  Email,
  Phone,
  VolumeUp,
  Wifi,
} from "@carbon/icons-react";
import { Trans, t } from "@lingui/macro";
import {
  Box,
  Button,
  InputAdornment,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { KeyboardEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { ReactRouterLinkWrapper } from "../MenuList/ReactRouterLinkWrapper";
import WSMBCNotificationPermissionType from "@WahooFitness/wsm-native/dist/esm/types/bc_notification_permission_type";
import useMaps from "./Maps/useMaps";
import { useCheckForOnboardingEffect } from "./useCheckForOnboardingEffect";
import { PluginListenerHandle } from "@capacitor/core";
import { crux_bolt_type_e } from "@WahooFitness/crux-js-display-cfg";
import { PLATFORM_ENUM } from "@WahooFitness/cloud-client-types/PlatformEnum";
import { BatteryPlus, CaretUpDown, Circle, LineVertical, Sun, Timer } from "@phosphor-icons/react";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import RemovePasscodeDrawer from "./RemovePasscodeDrawer";
import { useBikeBellAvailability } from "./Bell/useBikeBellAvailability";

type ConfigSection = {
  name?: string;
  items: MenuListItemType[];
  bottomContent?: string | JSX.Element;
  hidden?: boolean;
};

type NotificationStatusType = {
  supported: boolean;
  permitted: boolean;
  enabled: boolean;
};

const defaultNotificationStatus: NotificationStatusType = {
  supported: false,
  permitted: false,
  enabled: false,
};

const defaultAutoShutdownDuration = 15;
const workoutProfilesAlertKey = "workoutProfilesAlertShown";

function useDeviceSettings(appToken: string) {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { device, deviceError, deviceLoading, isDeviceConnected, reloadDevice } =
    usePairedDevice(appToken);

  // Get out if onboarding isn't finished.
  useCheckForOnboardingEffect(appToken, isDeviceConnected);

  const { update } = useGuardedUpdater();
  const { setDialog } = useDialogContext();
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();

  const { palette } = useTheme();

  const { platform } = useConfigContext();

  const { setDrawer, handleClose: handleCloseDrawer } = useDrawerContext();

  const { enqueueSnackbar } = useSnackbarContext();

  const { available: bikeBellAvailable } = useBikeBellAvailability(appToken);

  const [showWorkoutProfilesAlert, setShowWorkoutProfilesAlert] = useState(false);
  const onCloseWorkoutProfilesAlert = useCallback(() => {
    setShowWorkoutProfilesAlert(false);
  }, []);

  useEffect(() => {
    getStoredValue(workoutProfilesAlertKey).then((value) => {
      if (!value) {
        setShowWorkoutProfilesAlert(true);
        setStoredValue(workoutProfilesAlertKey, true);
      }
    });
  }, []);

  const {
    data: autoShutdownMinutes,
    error: autoShutdownError,
    isLoading: autoShutdownIsLoading,
    mutate: mutateAutoShutdownMinutes,
  } = useSWR(
    () => appToken && ["autoShutdownDuration", appToken],
    async ([_, appToken]) =>
      (await sensorManagerPlugin.bcGetAutoShutdownDurationMin({ appToken }))?.minutes,
    { fallbackData: defaultAutoShutdownDuration }
  );

  const autoShutdownEnabled = useMemo(() => (autoShutdownMinutes || 0) > 0, [autoShutdownMinutes]);

  const {
    data: displayMode,
    error: displayModeError,
    isLoading: displayModeIsLoading,
    mutate: mutateDisplayMode,
  } = useSWR(
    () => appToken && ["displayMode", appToken],
    async ([_, appToken]): Promise<PageColorMode> => {
      const mode = (await sensorManagerPlugin.bcGetLightDarkMode({ appToken }))?.mode;
      if (mode === PageColorMode.Auto) {
        return PageColorMode.Light;
      }
      return mode;
    },
    { fallbackData: PageColorMode.Light }
  );

  const { data: autoMaxBacklightCfgSupported } = useSWR(
    device?.sensorId !== undefined && isDeviceConnected && ["autoMaxBacklightCfgFeature", device],
    async ([_key, device]) => {
      const result = (await sensorManagerPlugin.getRemoteCfgStatus({ sensorId: device.sensorId }))
        ?.result?.features?.boltapp_backlight_auto_max;
      return result;
    },

    { fallbackData: false }
  );

  const {
    data: backlightCfg,
    error: backlightCfgError,
    isLoading: backlightCfgIsLoading,
    mutate: mutateBacklightCfg,
  } = useSWR(
    () => appToken && ["backLightCfg", appToken],
    async ([_, appToken]) =>
      (await sensorManagerPlugin.bcGetBacklightCfg({ appToken }))?.backlightCfg,
    { fallbackData: BacklightCfg.Auto }
  );

  const {
    data: backlightDurationSec,
    error: backlightDurationError,
    isLoading: backlightDurationIsLoading,
    mutate: mutateBacklightDurationSec,
  } = useSWR(
    () => appToken && ["backLightDurectionSec", appToken],
    async ([_, appToken]) =>
      (await sensorManagerPlugin.bcGetBacklightDurationSec({ appToken }))?.seconds?.toString() ||
      "",
    { fallbackData: "" }
  );

  const {
    data: alwaysRotateMapsEnabled,
    error: alwaysRotateMapsEnabledError,
    isLoading: alwaysRotateMapsEnabledIsLoading,
    mutate: mutateAlwaysRotateMapsEnabled,
  } = useSWR(
    () => appToken && ["alwaysRotateMapsEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetAlwaysRotateMapsEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: autoRerouteEnabled,
    error: autoRerouteEnabledError,
    isLoading: autoRerouteEnabledIsLoading,
    mutate: mutateAutoRerouteEnabled,
  } = useSWR(
    () => appToken && ["autoRerouteEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetAutoRerouteEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: emailNotificationStatus,
    error: emailNotificationStatusError,
    isLoading: emailAlertsEnabledIsLoading,
    mutate: mutateEmailAlertsEnabled,
  } = useSWR<NotificationStatusType>(
    () => appToken && ["emailAlertsEnabled", appToken],
    async ([_, appToken]) => {
      return {
        supported: !!(
          await sensorManagerPlugin.bcIsNotificationPermissionSupported({
            type: WSMBCNotificationPermissionType.EMAIL,
          })
        )?.supported,
        permitted: !!(
          await sensorManagerPlugin.bcIsNotificationPermissionPermitted({
            type: WSMBCNotificationPermissionType.EMAIL,
          })
        )?.permitted,
        enabled: !!(await sensorManagerPlugin.bcGetEmailAlertsEnabled({ appToken }))?.enabled,
      };
    },
    { fallbackData: defaultNotificationStatus }
  );

  const {
    data: msgNotificationStatus,
    error: msgNotificationStatusError,
    isLoading: msgAlertsEnabledIsLoading,
    mutate: mutateMsgAlertsEnabled,
  } = useSWR<NotificationStatusType>(
    () => appToken && ["msgAlertsEnabled", appToken],
    async ([_, appToken]) => {
      return {
        supported: !!(
          await sensorManagerPlugin.bcIsNotificationPermissionSupported({
            type: WSMBCNotificationPermissionType.MESSAGE,
          })
        )?.supported,
        permitted: !!(
          await sensorManagerPlugin.bcIsNotificationPermissionPermitted({
            type: WSMBCNotificationPermissionType.MESSAGE,
          })
        )?.permitted,
        enabled: !!(await sensorManagerPlugin.bcGetMsgAlertsEnabled({ appToken }))?.enabled,
      };
    },
    { fallbackData: defaultNotificationStatus }
  );

  const {
    data: phoneNotificationStatus,
    error: phoneNotificationStatusError,
    isLoading: phoneAlertsEnabledIsLoading,
    mutate: mutatePhoneAlertsEnabled,
  } = useSWR<NotificationStatusType>(
    () => appToken && ["phoneAlertsEnabled", appToken],
    async ([_, appToken]) => {
      return {
        supported: !!(
          await sensorManagerPlugin.bcIsNotificationPermissionSupported({
            type: WSMBCNotificationPermissionType.PHONE,
          })
        )?.supported,
        permitted: !!(
          await sensorManagerPlugin.bcIsNotificationPermissionPermitted({
            type: WSMBCNotificationPermissionType.PHONE,
          })
        )?.permitted,
        enabled: !!(await sensorManagerPlugin.bcGetPhoneAlertsEnabled({ appToken }))?.enabled,
      };
    },
    { fallbackData: defaultNotificationStatus }
  );

  const {
    data: notificationSoundEnabled,
    error: notificationSoundEnabledError,
    isLoading: notificationSoundEnabledIsLoading,
    mutate: mutateNotificationSoundEnabled,
  } = useSWR(
    () => appToken && ["notificationSoundEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetNotificationSoundEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: dndIntervalSec,
    error: dndIntervalSecError,
    isLoading: dndIntervalSecIsLoading,
    mutate: mutateDndIntervalSec,
  } = useSWR(
    () => appToken && ["dndIntervalSec", appToken],
    async ([_, appToken]) => (await sensorManagerPlugin.bcGetDndIntervalSec({ appToken }))?.seconds,
    { fallbackData: 0 }
  );

  const {
    data: usbAccessEnabled,
    error: usbAccessEnabledError,
    isLoading: usbAccessEnabledIsLoading,
    mutate: mutateUsbAccessEnabled,
  } = useSWR(
    () => appToken && ["usbAccessEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetUsbAccessEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: autoControlBikeLightsEnabled,
    error: autoControlBikeLightsEnabledError,
    isLoading: autoControlBikeLightsEnabledIsLoading,
    mutate: mutateAutoControlBikeLightsEnabled,
  } = useSWR(
    () => appToken && ["autoControlBikeLightsEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetAutoControlBikeLightsEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: usbFitExportEnabled,
    error: usbFitExportEnabledError,
    isLoading: usbFitExportEnabledIsLoading,
    mutate: mutateUsbFitExportEnabled,
  } = useSWR(
    () => appToken && ["usbFitExportEnabled", appToken],
    async ([_, appToken]) =>
      !!(await sensorManagerPlugin.bcGetUsbFitExportEnabled({ appToken }))?.enabled,
    { fallbackData: false }
  );

  const {
    data: devicePasscodeState,
    error: devicePasscodeStateError,
    isLoading: devicePasscodeStateIsLoading,
    mutate: mutateDevicePasscodeState,
  } = useSWR(
    () => appToken && ["devicePasscodeState", appToken],
    async ([_, appToken]) => (await sensorManagerPlugin.bcGetPasscodeState({ appToken }))?.state,
    { fallbackData: WSMBoltAppPasscodeState.NotConfigured }
  );

  const { data: allowUsbAccessCfgSupported } = useSWR(
    device?.sensorId !== undefined && isDeviceConnected && ["allowUsbAccessCfgFeature", device],
    async ([_key, device]) => {
      const result = (await sensorManagerPlugin.getRemoteCfgStatus({ sensorId: device.sensorId }))
        ?.result?.features?.usb_access;
      return result;
    },

    { fallbackData: false }
  );

  const { data: allowUsbFitExportCfgSupported } = useSWR(
    device?.sensorId !== undefined && isDeviceConnected && ["usbFitExportEnabled", device],
    async ([_key, device]) => {
      const result = (await sensorManagerPlugin.getRemoteCfgStatus({ sensorId: device.sensorId }))
        ?.result?.features?.usb_fit_export;
      return result;
    },

    { fallbackData: false }
  );

  const { data: allowPasscodeCfgSupported } = useSWR(
    device?.sensorId !== undefined && isDeviceConnected && ["allowPasscodeCfgFeature", device],
    async ([_key, device]) => {
      const result = (await sensorManagerPlugin.getRemoteCfgStatus({ sensorId: device.sensorId }))
        ?.result?.features?.passcode;
      return result && devicePasscodeState === WSMBoltAppPasscodeState.Enabled;
    },

    { fallbackData: false }
  );

  const { data: autoControlBikeLightsSupported } = useSWR(
    device?.sensorId !== undefined && isDeviceConnected && ["autoControlBikeLightsFeature", device],
    async ([_key, device]) => {
      const result = (await sensorManagerPlugin.getRemoteCfgStatus({ sensorId: device.sensorId }))
        ?.result?.features?.smart_light_control;
      return result;
    },

    { fallbackData: false }
  );

  useEffect(() => {
    const listeners: Promise<PluginListenerHandle>[] = [
      sensorManagerPlugin.addListener("autoShutDownDurationChange", (event) => {
        if (event.appToken === appToken) mutateAutoShutdownMinutes();
      }),
      sensorManagerPlugin.addListener("dndIntervalChange", (event) => {
        if (event.appToken === appToken) mutateDndIntervalSec();
      }),
      sensorManagerPlugin.addListener("emailAlertsEnabledChange", (event) => {
        if (event.appToken === appToken) mutateEmailAlertsEnabled();
      }),
      sensorManagerPlugin.addListener("msgAlertsEnabledChange", (event) => {
        if (event.appToken === appToken) mutateMsgAlertsEnabled();
      }),
      sensorManagerPlugin.addListener("phoneAlertsEnabledChange", (event) => {
        if (event.appToken === appToken) mutatePhoneAlertsEnabled();
      }),
      sensorManagerPlugin.addListener("backlightCfgChange", (event) => {
        if (event.appToken === appToken) mutateBacklightCfg();
      }),
      sensorManagerPlugin.addListener("backlightDurationSecChange", (event) => {
        if (event.appToken === appToken) mutateBacklightDurationSec();
      }),
      sensorManagerPlugin.addListener("lightDarkModeChange", (event) => {
        if (event.appToken === appToken) mutateDisplayMode();
      }),
      sensorManagerPlugin.addListener("alwaysRotateMapsEnabledChange", (event) => {
        if (event.appToken === appToken) mutateAlwaysRotateMapsEnabled();
      }),
      sensorManagerPlugin.addListener("autoRerouteEnabledChange", (event) => {
        if (event.appToken === appToken) mutateAutoRerouteEnabled();
      }),
      sensorManagerPlugin.addListener("notificationSoundEnabledChange", (event) => {
        if (event.appToken === appToken) mutateNotificationSoundEnabled();
      }),
      sensorManagerPlugin.addListener("usbAccessEnabledChange", (event) => {
        if (event.appToken === appToken) mutateUsbAccessEnabled();
      }),
      sensorManagerPlugin.addListener("passcodeStateChange", (event) => {
        if (event.appToken === appToken) mutateDevicePasscodeState();
      }),
      sensorManagerPlugin.addListener("autoControlBikeLightsEnabledChange", (event) => {
        if (event.appToken === appToken) mutateAutoControlBikeLightsEnabled();
      }),
      sensorManagerPlugin.addListener("usbFitExportEnabledChange", (event) => {
        if (event.appToken === appToken) mutateUsbFitExportEnabled();
      }),
    ];
    return () => {
      listeners.forEach(async (listener) => (await listener).remove());
    };
  }, [
    appToken,
    mutateAlwaysRotateMapsEnabled,
    mutateAutoRerouteEnabled,
    mutateAutoShutdownMinutes,
    mutateBacklightCfg,
    mutateBacklightDurationSec,
    mutateDevicePasscodeState,
    mutateDisplayMode,
    mutateDndIntervalSec,
    mutateEmailAlertsEnabled,
    mutateMsgAlertsEnabled,
    mutateNotificationSoundEnabled,
    mutatePhoneAlertsEnabled,
    mutateUsbAccessEnabled,
    mutateAutoControlBikeLightsEnabled,
    mutateUsbFitExportEnabled,
    sensorManagerPlugin,
  ]);

  useEffect(() => {
    const refreshListener = addRefreshListener(() => {
      mutateEmailAlertsEnabled();
      mutateMsgAlertsEnabled();
      mutatePhoneAlertsEnabled();
    });
    return () => {
      removeRefreshListener(refreshListener);
    };
  }, [
    addRefreshListener,
    mutateEmailAlertsEnabled,
    mutateMsgAlertsEnabled,
    mutatePhoneAlertsEnabled,
    removeRefreshListener,
  ]);

  const updateAutoShutdownDuration = useCallback(
    async (minutes: number) => {
      if (!appToken) return;
      mutateAutoShutdownMinutes(minutes, { revalidate: false });
      return await sensorManagerPlugin.bcSetAutoShutdownDurationMin({
        appToken,
        minutes,
      });
    },
    [appToken, mutateAutoShutdownMinutes, sensorManagerPlugin]
  );

  const updateDisplayMode = useCallback(
    async (mode: PageColorMode) => {
      if (!appToken) return;
      mutateDisplayMode(mode, { revalidate: false });
      return await sensorManagerPlugin.bcSetLightDarkMode({ appToken, mode });
    },
    [appToken, mutateDisplayMode, sensorManagerPlugin]
  );

  const updateBacklightCfg = useCallback(
    async (backlightCfg: BacklightCfg) => {
      if (!appToken) return;
      mutateBacklightCfg(backlightCfg, { revalidate: false });
      return await sensorManagerPlugin.bcSetBacklightCfg({
        appToken,
        backlightCfg,
      });
    },
    [appToken, mutateBacklightCfg, sensorManagerPlugin]
  );

  const [backlightDurationSecondsValid, setBacklightDurationSecondsValid] = useState<boolean>(true);

  const updateBacklightDurationSec = useCallback(
    async (seconds: string) => {
      if (!appToken) return;
      mutateBacklightDurationSec(seconds, { revalidate: false });
      if (+seconds < 256) {
        setBacklightDurationSecondsValid(true);
        await sensorManagerPlugin.bcSetBacklightDurationSec({
          appToken,
          seconds: +seconds,
        });
      } else {
        setBacklightDurationSecondsValid(false);
      }
    },
    [appToken, mutateBacklightDurationSec, sensorManagerPlugin]
  );

  const updateAlwaysRotateMapsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      mutateAlwaysRotateMapsEnabled(enabled, { revalidate: false });
      return await sensorManagerPlugin.bcSetAlwaysRotateMapsEnabled({
        appToken,
        enabled,
      });
    },
    [appToken, mutateAlwaysRotateMapsEnabled, sensorManagerPlugin]
  );

  const updateAutoControlBikeLightsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      update(
        async () => {
          const result = await sensorManagerPlugin.bcSetAutoControlBikeLightsEnabled({
            appToken,
            enabled,
          });
          if (!result.completed) {
            throw new Error("Failed to update Auto Control Bike Lights setting");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              mutateAutoControlBikeLightsEnabled(enabled, { revalidate: false });
            }
            return result;
          },
          errorMessage: t`Failed to change Auto Control Bike Lights.`,
        }
      );
    },
    [appToken, mutateAutoControlBikeLightsEnabled, sensorManagerPlugin, update]
  );

  const updateUsbAccessEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      update(
        async () => {
          const result = await sensorManagerPlugin.bcSetUsbAccessEnabled({ appToken, enabled });
          if (!result.completed) {
            throw new Error("Failed to update USB access setting");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              mutateUsbAccessEnabled(enabled, { revalidate: false });
            }
            return result;
          },
          errorMessage: t`Failed to change USB access.`,
        }
      );
    },
    [appToken, mutateUsbAccessEnabled, sensorManagerPlugin, update]
  );

  const updateUsbFitExportEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      update(
        async () => {
          const result = await sensorManagerPlugin.bcSetUsbFitExportEnabled({ appToken, enabled });
          if (!result.completed) {
            throw new Error("Failed to update .FIT access setting");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              mutateUsbFitExportEnabled(enabled, { revalidate: false });
            }
            return result;
          },
          errorMessage: t`Failed to change .FIT access.`,
        }
      );
    },
    [appToken, mutateUsbFitExportEnabled, sensorManagerPlugin, update]
  );

  const updateDevicePasscodeState = useCallback(
    async (state: WSMBoltAppPasscodeState) => {
      if (!appToken) return;
      update(
        async () => {
          const result = await sensorManagerPlugin.bcSetPasscodeState({ appToken, state });
          if (!result.completed) {
            throw new Error("Failed to update passcode state");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              mutateDevicePasscodeState(state, { revalidate: false });
              if (state === WSMBoltAppPasscodeState.Disabled) {
                enqueueSnackbar({
                  message: t`Passcode has been removed from ELEMNT`,
                  severity: "success",
                });
              }
            }
            return result;
          },
          errorMessage: t`Failed to remove passcode.`,
        }
      );
    },
    [appToken, enqueueSnackbar, mutateDevicePasscodeState, sensorManagerPlugin, update]
  );

  const updateAutoRerouteEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      mutateAutoRerouteEnabled(enabled, { revalidate: false });
      return await sensorManagerPlugin.bcSetAutoRerouteEnabled({ appToken, enabled });
    },
    [appToken, mutateAutoRerouteEnabled, sensorManagerPlugin]
  );

  const updateEmailAlertsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken || !emailNotificationStatus) return;
      mutateEmailAlertsEnabled({ ...emailNotificationStatus, enabled }, { revalidate: false });
      return await sensorManagerPlugin.bcSetEmailAlertsEnabled({ appToken, enabled });
    },
    [appToken, emailNotificationStatus, mutateEmailAlertsEnabled, sensorManagerPlugin]
  );

  const updateMsgAlertsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken || !msgNotificationStatus) return;
      mutateMsgAlertsEnabled({ ...msgNotificationStatus, enabled }, { revalidate: false });
      return await sensorManagerPlugin.bcSetMsgAlertsEnabled({ appToken, enabled });
    },
    [appToken, msgNotificationStatus, mutateMsgAlertsEnabled, sensorManagerPlugin]
  );

  const updatePhoneAlertsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken || !phoneNotificationStatus) return;
      mutatePhoneAlertsEnabled({ ...phoneNotificationStatus, enabled }, { revalidate: false });
      return await sensorManagerPlugin.bcSetPhoneAlertsEnabled({ appToken, enabled });
    },
    [appToken, mutatePhoneAlertsEnabled, phoneNotificationStatus, sensorManagerPlugin]
  );

  const updateNotificationSoundEnabled = useCallback(
    async (enabled: boolean) => {
      if (!appToken) return;
      mutateNotificationSoundEnabled(enabled, { revalidate: false });
      return await sensorManagerPlugin.bcSetNotificationSoundEnabled({ appToken, enabled });
    },
    [appToken, mutateNotificationSoundEnabled, sensorManagerPlugin]
  );

  const updateDndIntervalSec = useCallback(
    async (seconds: number) => {
      if (!appToken) return;
      mutateDndIntervalSec(seconds, { revalidate: false });
      return await sensorManagerPlugin.bcSetDndIntervalSec({ appToken, seconds });
    },
    [appToken, mutateDndIntervalSec, sensorManagerPlugin]
  );

  const handleChangeAutoShutdownDuration = useCallback(
    (_: any, value: number) => {
      if (value !== null) {
        updateAutoShutdownDuration(value);
      }
    },
    [updateAutoShutdownDuration]
  );

  const handleChangeDisplayMode = useCallback(
    (_: any, value: PageColorMode) => {
      if (value !== null) {
        updateDisplayMode(value);
      }
    },
    [updateDisplayMode]
  );

  const handleChangeBacklightCfg = useCallback(
    (value: number) => {
      if (value !== null) {
        updateBacklightCfg(value);
      }
      handleCloseDrawer();
    },
    [handleCloseDrawer, updateBacklightCfg]
  );

  const handleChangeBacklightDurationSec = useCallback(
    (event: any) => {
      updateBacklightDurationSec(event.target.value);
    },
    [updateBacklightDurationSec]
  );

  const toggleAlwaysRotateMapsEnabled = useCallback(() => {
    updateAlwaysRotateMapsEnabled(!alwaysRotateMapsEnabled);
  }, [alwaysRotateMapsEnabled, updateAlwaysRotateMapsEnabled]);

  const toggleAutoRerouteEnabled = useCallback(() => {
    updateAutoRerouteEnabled(!autoRerouteEnabled);
  }, [autoRerouteEnabled, updateAutoRerouteEnabled]);

  const toggleUsbAccessEnabled = useCallback(() => {
    updateUsbAccessEnabled(!usbAccessEnabled);
  }, [usbAccessEnabled, updateUsbAccessEnabled]);

  const toggleUsbFitExportEnabled = useCallback(() => {
    updateUsbFitExportEnabled(!usbFitExportEnabled);
  }, [usbFitExportEnabled, updateUsbFitExportEnabled]);

  const toggleAutoControlBikeLightsEnabled = useCallback(() => {
    updateAutoControlBikeLightsEnabled(!autoControlBikeLightsEnabled);
  }, [autoControlBikeLightsEnabled, updateAutoControlBikeLightsEnabled]);

  const toggleEmailAlertsEnabled = useCallback(() => {
    if (!emailNotificationStatus) {
      return;
    }
    if (!emailNotificationStatus.supported) {
      setDialog({
        open: true,
        title: t`Feature not supported`,
        body: t`Email notifications are not supported on your device.`,
        actions: [{ text: t`Dismiss` }],
      });
      return;
    }
    if (!emailNotificationStatus.permitted) {
      postEvent("requestPermission", { permissionType: "emailNotifications" });
      return;
    }
    updateEmailAlertsEnabled(!emailNotificationStatus.enabled);
  }, [emailNotificationStatus, setDialog, updateEmailAlertsEnabled]);

  const toggleMsgAlertsEnabled = useCallback(() => {
    if (!msgNotificationStatus) {
      return;
    }
    if (!msgNotificationStatus.supported) {
      setDialog({
        open: true,
        title: t`Feature not supported`,
        body: t`Message notifications are not supported on your device.`,
        actions: [{ text: t`Dismiss` }],
      });
      return;
    }
    if (!msgNotificationStatus.permitted) {
      postEvent("requestPermission", { permissionType: "msgNotifications" });
      return;
    }
    updateMsgAlertsEnabled(!msgNotificationStatus.enabled);
  }, [msgNotificationStatus, setDialog, updateMsgAlertsEnabled]);

  const togglePhoneAlertsEnabled = useCallback(() => {
    if (!phoneNotificationStatus) {
      return;
    }
    if (!phoneNotificationStatus.supported) {
      setDialog({
        open: true,
        title: t`Feature not supported`,
        body: t`Phone notifications are not supported on your device.`,
        actions: [{ text: t`Dismiss` }],
      });
      return;
    }
    if (!phoneNotificationStatus.permitted) {
      postEvent("requestPermission", { permissionType: "phoneNotifications" });
      return;
    }
    updatePhoneAlertsEnabled(!phoneNotificationStatus.enabled);
  }, [phoneNotificationStatus, setDialog, updatePhoneAlertsEnabled]);

  const toggleNotificationSoundEnabled = useCallback(() => {
    updateNotificationSoundEnabled(!notificationSoundEnabled);
  }, [notificationSoundEnabled, updateNotificationSoundEnabled]);

  const handleChangeDndIntervalSec = useCallback(
    (_: any, value: number) => {
      if (value !== null) {
        updateDndIntervalSec(value);
      }
    },
    [updateDndIntervalSec]
  );

  const enableAutoShutdown = useCallback(async () => {
    if (!appToken) return;
    updateAutoShutdownDuration(defaultAutoShutdownDuration);
  }, [appToken, updateAutoShutdownDuration]);

  const disableAutoShutdown = useCallback(async () => {
    if (!appToken) return;
    updateAutoShutdownDuration(0);
  }, [appToken, updateAutoShutdownDuration]);

  const autoShutdownListItem = useMemo(
    () => ({
      id: "autoShutdown",
      content: t`Auto shutdown`,
      variant: MenuListItemVariant.Accordion,
      expanded: autoShutdownEnabled,
      actionComponent: <BlueSwitch checked={autoShutdownEnabled} />,
      action: () => (autoShutdownEnabled ? disableAutoShutdown() : enableAutoShutdown()),
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={autoShutdownMinutes}
            onChange={handleChangeAutoShutdownDuration}
          >
            <ToggleButton value={15}>
              <Trans>15 min</Trans>
            </ToggleButton>
            <ToggleButton value={30}>
              <Trans>30 min</Trans>
            </ToggleButton>
            <ToggleButton value={60}>
              <Trans>60 min</Trans>
            </ToggleButton>
            <ToggleButton value={120}>
              <Trans>120 min</Trans>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ),
    }),
    [
      autoShutdownEnabled,
      autoShutdownMinutes,
      disableAutoShutdown,
      enableAutoShutdown,
      handleChangeAutoShutdownDuration,
    ]
  );

  const displayModeListItem = useMemo(
    () => ({
      id: "displayMode",
      content: t`Display mode`,
      variant: MenuListItemVariant.NoAction,
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1} mt={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={displayMode}
            onChange={handleChangeDisplayMode}
          >
            <ToggleButton value={PageColorMode.Light}>
              <Trans>Light</Trans>
            </ToggleButton>
            <ToggleButton value={PageColorMode.Dark}>
              <Trans>Dark</Trans>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ),
      hidden:
        device?.boltType === crux_bolt_type_e.ROAM_1_1 ||
        device?.boltType === crux_bolt_type_e.ROAM ||
        device?.boltType === crux_bolt_type_e.BOLT,
    }),
    [device?.boltType, displayMode, handleChangeDisplayMode]
  );

  const isInvalidNumberChar = (value: string) => {
    return ["-", "e", "!"].includes(value);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback((event) => {
    if (isInvalidNumberChar(event.key)) {
      event.preventDefault();
    }
  }, []);

  const backlightSubtitleContent = useMemo(() => {
    switch (backlightCfg) {
      case BacklightCfg.Auto:
        return (
          <ListItemText secondary={t`Adapts to ambient light conditions to optimize performance`} />
        );
      case BacklightCfg.AutoMax:
        return (
          <ListItemText
            secondary={t`Adapts to ambient light conditions, prioritizing visibility over battery`}
          />
        );
      case BacklightCfg.Button:
        return <ListItemText secondary={t`Turns off after set time`} />;
      case BacklightCfg.Off:
        return <ListItemText secondary={t`No backlight`} />;
      case BacklightCfg.On:
        return <ListItemText secondary={t`Constant backlight`} />;
      default:
        return null;
    }
  }, [backlightCfg]);

  const backlightCustomTimeContent = useMemo(() => {
    return (
      <TextField
        value={backlightDurationSec?.toString() || ""}
        label={t`Time (sec)`}
        type="number"
        onChange={handleChangeBacklightDurationSec}
        onKeyDown={handleKeyDown}
        helperText={backlightDurationSecondsValid ? "" : t`Enter a value from 0 to 255`}
        error={!backlightDurationSecondsValid}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Trans>sec</Trans>
            </InputAdornment>
          ),
        }}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
          min: 0,
          max: 255,
          sx: {
            "::-webkit-inner-spin-button, ::-webkit-outer-spin-button": {
              "-webkit-appearance": "none",
              margin: 0,
            },
            "-moz-appearance": "textfield",
          },
          "data-testid": "backlightDurationInput",
        }}
      >
        <Trans>Duration (sec)</Trans>
      </TextField>
    );
  }, [
    backlightDurationSec,
    backlightDurationSecondsValid,
    handleChangeBacklightDurationSec,
    handleKeyDown,
  ]);

  const backlightDrawerConfigItems = useMemo(
    () =>
      [
        {
          id: "backlight-off",
          content: t`Off`,
          secondaryContent: t`No backlight`,
          variant: MenuListItemVariant.Action,
          actionComponent:
            backlightCfg === BacklightCfg.Off ? (
              <Box>
                <Checkmark size={24} fill={palette.info.main} />
              </Box>
            ) : undefined,
          action: () => {
            handleChangeBacklightCfg(BacklightCfg.Off);
          },
          icon: (
            <Box my={0.5}>
              <FancyIcon color="info" size="small" icon={<Circle size={24} />}></FancyIcon>
            </Box>
          ),
        },
        {
          id: "backlight-button",
          content: t`Time`,
          secondaryContent: t`Turns off after set time`,
          variant: MenuListItemVariant.Action,
          actionComponent:
            backlightCfg === BacklightCfg.Button ? (
              <Box>
                <Checkmark size={24} fill={palette.info.main} />
              </Box>
            ) : undefined,
          action: () => {
            handleChangeBacklightCfg(BacklightCfg.Button);
          },
          icon: (
            <Box my={0.5}>
              <FancyIcon color="info" size="small" icon={<Timer size={24} />}></FancyIcon>
            </Box>
          ),
        },
        {
          id: "backlight-auto",
          content: t`Auto`,
          secondaryContent: t`Adapts to ambient light conditions to optimize performance`,
          variant: MenuListItemVariant.Action,
          actionComponent:
            backlightCfg === BacklightCfg.Auto ? (
              <Box>
                <Checkmark size={24} fill={palette.info.main} />
              </Box>
            ) : undefined,
          action: () => {
            handleChangeBacklightCfg(BacklightCfg.Auto);
          },
          icon: (
            <Box my={0.5}>
              <FancyIcon color="info" size="small" icon={<BatteryPlus size={24} />}></FancyIcon>
            </Box>
          ),
        },
        {
          id: "backlight-auto-max",
          content: t`Auto Max`,
          secondaryContent: t`Adapts to ambient light conditions, prioritizing visibility over battery`,
          variant: MenuListItemVariant.Action,
          hidden: !autoMaxBacklightCfgSupported,
          actionComponent:
            backlightCfg === BacklightCfg.AutoMax ? (
              <Box>
                <Checkmark size={24} fill={palette.info.main} />
              </Box>
            ) : undefined,
          action: () => {
            handleChangeBacklightCfg(BacklightCfg.AutoMax);
          },
          icon: (
            <Box my={0.5}>
              <FancyIcon color="info" size="small" icon={<Sun size={24} />}></FancyIcon>
            </Box>
          ),
        },
        {
          id: "backlight-on",
          content: t`On`,
          secondaryContent: t`Constant backlight`,
          variant: MenuListItemVariant.Action,
          actionComponent:
            backlightCfg === BacklightCfg.On ? (
              <Box>
                <Checkmark size={24} fill={palette.info.main} />
              </Box>
            ) : undefined,
          action: () => {
            handleChangeBacklightCfg(BacklightCfg.On);
          },
          icon: (
            <Box my={0.5}>
              <FancyIcon color="info" size="small" icon={<LineVertical size={24} />}></FancyIcon>
            </Box>
          ),
        },
      ] as MenuListItemType[],
    [backlightCfg, autoMaxBacklightCfgSupported, handleChangeBacklightCfg, palette.info.main]
  );

  const showBacklightConfigDrawer = useCallback(() => {
    setDrawer({
      open: true,
      title: t`Backlight`,
      hidePuller: true,
      children: <MenuList listItems={backlightDrawerConfigItems}></MenuList>,
    });
  }, [backlightDrawerConfigItems, setDrawer]);

  const currentBacklightString = useMemo(() => {
    switch (backlightCfg) {
      case BacklightCfg.Off:
        return t`Off`;
      case BacklightCfg.Button:
        return t`Time`;
      case BacklightCfg.On:
        return t`On`;
      case BacklightCfg.Auto:
        return t`Auto`;
      case BacklightCfg.AutoMax:
        return t`Auto Max`;
      default:
        return t`Backlight`;
    }
  }, [backlightCfg]);

  const newBacklightContent = useMemo(() => {
    return (
      <Box display="flex" flexDirection="column" rowGap={2} mt={1}>
        <Button
          variant="outlined"
          size="large"
          color="primary"
          onClick={showBacklightConfigDrawer}
          endIcon={<CaretUpDown size={16} color={palette.primary.main} />}
          fullWidth
          sx={{
            borderRadius: "8px",
            justifyContent: "space-between",
            "& .MuiButton-endIcon": {
              marginLeft: "auto",
            },
            textAlign: "left",
            height: "56px",
          }}
        >
          <Typography variant="ui-base" sx={{ ml: -1 }}>
            {currentBacklightString}
          </Typography>
        </Button>
        {backlightCfg === BacklightCfg.Button && backlightCustomTimeContent}
        {backlightSubtitleContent}
      </Box>
    );
  }, [
    backlightCfg,
    backlightCustomTimeContent,
    backlightSubtitleContent,
    currentBacklightString,
    palette.primary.main,
    showBacklightConfigDrawer,
  ]);

  const backlightListItem = useMemo(
    () => ({
      id: "backlight",
      content: t`Backlight`,
      variant: MenuListItemVariant.Action,
      secondaryContent: newBacklightContent,
    }),
    [newBacklightContent]
  );

  const displayConfig: ConfigSection = useMemo(
    () => ({
      name: t`Display`,
      items: [autoShutdownListItem, displayModeListItem, backlightListItem],
    }),
    [autoShutdownListItem, backlightListItem, displayModeListItem]
  );

  const wifiConfig: ConfigSection = useMemo(
    () => ({
      name: t`Wi-Fi`,
      items: [
        {
          id: "wifiNetwork",
          icon: <Wifi size={24} />,
          content: t`Wi-Fi network`,
          secondaryContent: t`ELEMNT uses Wi-Fi to automatically upload workouts, update firmware, and download maps & routes`,
          variant: MenuListItemVariant.InternalLink,
          linkLocation: `/device-config/${appToken}/wifi`,
          "data-testid": "settings.device.wifi",
          disabled: !isDeviceConnected,
          linkComponent: ReactRouterLinkWrapper,
        },
      ],
    }),
    [isDeviceConnected, appToken]
  );

  const rotateMapsListItem = useMemo(
    () => ({
      id: "rotateMaps",
      content: t`Always rotate maps`,
      secondaryContent: t`When enabled, the map will rotate so you are always heading towards the top of the display, similar to when using routes.`,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={alwaysRotateMapsEnabled} />,
      action: toggleAlwaysRotateMapsEnabled,
      "data-testid": "settings.device.rotateMaps",
    }),
    [alwaysRotateMapsEnabled, toggleAlwaysRotateMapsEnabled]
  );

  const autoRerouteListItem = useMemo(
    () => ({
      id: "reroute",
      content: t`Automatically reroute`,
      secondaryContent: t`When enabled, we’ll route you back to your original route if you go off-route.`,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={autoRerouteEnabled} />,
      action: toggleAutoRerouteEnabled,
      "data-testid": "settings.device.reroute",
    }),
    [autoRerouteEnabled, toggleAutoRerouteEnabled]
  );

  const { mapPack, mapPackIsLoading } = useMaps(appToken || "", true);

  const mapsNeedUpdate = useMemo(
    () => !mapPackIsLoading && mapPack?.mapUpgradeRequired,
    [mapPack?.mapUpgradeRequired, mapPackIsLoading]
  );
  const manageMapsListItem = useMemo(
    () => ({
      id: "manageMaps",
      icon: mapsNeedUpdate ? <CircleFill size={24} fill={palette.error.main} /> : undefined,
      content: t`Manage maps`,
      variant: MenuListItemVariant.InternalLink,
      linkComponent: ReactRouterLinkWrapper,
      linkLocation: `/device-config/${appToken}/maps`,
      "data-testid": "settings.device.manageMaps",
    }),
    [appToken, mapsNeedUpdate, palette.error.main]
  );

  const navConfig: ConfigSection = useMemo(
    () => ({
      name: t`Navigation`,
      items: [rotateMapsListItem, autoRerouteListItem, manageMapsListItem],
    }),
    [rotateMapsListItem, autoRerouteListItem, manageMapsListItem]
  );

  const autoControlBikeLightsListItem = useMemo(
    () => ({
      id: "autoControlBikeLights",
      content: t`Auto control bike lights`,
      secondaryContent: t`When enabled, ELEMNT will automatically turn on all connected ANT+ lights at the start of rides and off at the end. Lights will turn on to the last used light mode.`,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={autoControlBikeLightsEnabled} />,
      action: toggleAutoControlBikeLightsEnabled,
      hidden: !autoControlBikeLightsSupported,
    }),
    [
      autoControlBikeLightsEnabled,
      autoControlBikeLightsSupported,
      toggleAutoControlBikeLightsEnabled,
    ]
  );

  const sensorConfig: ConfigSection = useMemo(
    () => ({
      name: t`Sensors`,
      items: [autoControlBikeLightsListItem],
    }),
    [autoControlBikeLightsListItem]
  );

  const usbAccessListItem = useMemo(
    () => ({
      id: "usbAccess",
      content: t`Allow USB access`,
      secondaryContent: t`When enabled, allows certain files to be accessed via USB. These include (but are not limited to) USB-loaded planned workouts and routes.`,
      hidden: !allowUsbAccessCfgSupported,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={usbAccessEnabled} />,
      action: toggleUsbAccessEnabled,
    }),
    [allowUsbAccessCfgSupported, toggleUsbAccessEnabled, usbAccessEnabled]
  );

  const usbFitExportListItem = useMemo(
    () => ({
      id: "fitAccess",
      content: t`Allow .FIT file access`,
      secondaryContent: t`When enabled, .FIT files will be copied and accessible via USB.`,
      hidden: !allowUsbFitExportCfgSupported,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={usbFitExportEnabled} />,
      action: toggleUsbFitExportEnabled,
    }),
    [allowUsbFitExportCfgSupported, toggleUsbFitExportEnabled, usbFitExportEnabled]
  );

  const handleRemovePasscode = useCallback(
    (value: boolean) => {
      if (value) {
        updateDevicePasscodeState(WSMBoltAppPasscodeState.Disabled);
      }
      handleCloseDrawer();
    },
    [handleCloseDrawer, updateDevicePasscodeState]
  );

  const showRemovePasscodeDrawer = useCallback(() => {
    setDrawer({
      open: true,
      hidePuller: true,
      hideClose: true,
      hideDivider: true,
      hideHeader: true,
      children: (
        <RemovePasscodeDrawer
          onRemove={() => handleRemovePasscode(true)}
          onCancel={handleCloseDrawer}
        />
      ),
    });
  }, [handleCloseDrawer, handleRemovePasscode, setDrawer]);

  const devicePasscodeListItem = useMemo(
    () => ({
      id: "devicePasscode",
      content: t`Remove passcode`,
      hidden: !allowPasscodeCfgSupported,
      variant: MenuListItemVariant.Action,
      action: showRemovePasscodeDrawer,
      actionComponent: <ChevronRightIcon sx={{ color: palette.action.active }} />,
    }),
    [allowPasscodeCfgSupported, palette.action.active, showRemovePasscodeDrawer]
  );

  const messageNotificationListItem = useMemo(
    () => ({
      id: "messages",
      icon: <Chat size={24} />,
      content: t`Show messages`,
      variant: MenuListItemVariant.Action,
      actionComponent: (
        <BlueSwitch
          disabled={!msgNotificationStatus?.supported || !msgNotificationStatus.permitted}
          checked={
            msgNotificationStatus?.supported &&
            msgNotificationStatus.permitted &&
            msgNotificationStatus.enabled
          }
        />
      ),
      action: toggleMsgAlertsEnabled,
    }),
    [
      msgNotificationStatus?.enabled,
      msgNotificationStatus?.permitted,
      msgNotificationStatus?.supported,
      toggleMsgAlertsEnabled,
    ]
  );

  const phoneNotificationListItem = useMemo(
    () => ({
      id: "phoneCalls",
      icon: <Phone size={24} />,
      content: t`Show phone calls`,
      variant: MenuListItemVariant.Action,
      actionComponent: (
        <BlueSwitch
          disabled={!phoneNotificationStatus?.supported || !phoneNotificationStatus.permitted}
          checked={
            phoneNotificationStatus?.supported &&
            phoneNotificationStatus.permitted &&
            phoneNotificationStatus.enabled
          }
        />
      ),
      action: togglePhoneAlertsEnabled,
    }),
    [
      phoneNotificationStatus?.enabled,
      phoneNotificationStatus?.permitted,
      phoneNotificationStatus?.supported,
      togglePhoneAlertsEnabled,
    ]
  );

  const emailNotificationListItem = useMemo(
    () => ({
      id: "emails",
      icon: <Email size={24} />,
      content: t`Show emails`,
      variant: MenuListItemVariant.Action,
      actionComponent: (
        <BlueSwitch
          disabled={!emailNotificationStatus?.supported || !emailNotificationStatus.permitted}
          checked={
            emailNotificationStatus?.supported &&
            emailNotificationStatus.permitted &&
            emailNotificationStatus.enabled
          }
        />
      ),
      action: toggleEmailAlertsEnabled,
    }),
    [
      emailNotificationStatus?.enabled,
      emailNotificationStatus?.permitted,
      emailNotificationStatus?.supported,
      toggleEmailAlertsEnabled,
    ]
  );

  const notificationSoundListItem = useMemo(
    () => ({
      id: "notificationSound",
      icon: <VolumeUp size={24} />,
      content: t`Play sounds for notifications`,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={notificationSoundEnabled} />,
      action: toggleNotificationSoundEnabled,
    }),
    [notificationSoundEnabled, toggleNotificationSoundEnabled]
  );

  const dndNotificationListItem = useMemo(
    () => ({
      id: "dnd",
      icon: <Asleep size={24} />,
      content: t`Do not disturb`,
      variant: MenuListItemVariant.Accordion,
      expanded: true,
      secondaryContent: (
        <Box display="flex" flexDirection="column" rowGap={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={dndIntervalSec}
            onChange={handleChangeDndIntervalSec}
          >
            <ToggleButton value={0}>
              <Trans>Off</Trans>
            </ToggleButton>
            <ToggleButton value={300}>
              <Trans>5 min</Trans>
            </ToggleButton>
            <ToggleButton value={900}>
              <Trans>15 min</Trans>
            </ToggleButton>
            <ToggleButton value={1800}>
              <Trans>30 min</Trans>
            </ToggleButton>
          </ToggleButtonGroup>
          <ListItemText
            secondary={t`When a notification pops up on your ELEMNT, pressing ‘DND’ will mute notifications for this amount of time.`}
          />
        </Box>
      ),
    }),
    [dndIntervalSec, handleChangeDndIntervalSec]
  );

  const notificationSubtitleContent = useMemo(
    () =>
      platform === PLATFORM_ENUM.ios ? (
        <Typography variant="prose-3xs" color="text.secondary">
          <Trans>
            If alerts aren’t appearing as expected, open{" "}
            <Typography variant="prose-3xs-medium" color="text.secondary">
              iOS Settings {">"} Notifications
            </Typography>{" "}
            and ensure ‘Show Previews’ is set to either ‘Always’ or ‘When Unlocked’.
          </Trans>
        </Typography>
      ) : (
        <Typography variant="prose-3xs" color="text.secondary">
          <Trans>
            To enable alerts, go to your phone{" "}
            <Typography variant="prose-3xs-medium" color="text.secondary">
              Settings {">"} Apps {">"} Wahoo.
            </Typography>{" "}
            Then, tap the three dots in the upper right hand corner to allow restricted settings.
            Return to the Wahoo app to finish adding the permissions.
          </Trans>
        </Typography>
      ),
    [platform]
  );

  const notificationsConfig: ConfigSection = useMemo(
    () => ({
      name: t`Notifications`,
      bottomContent: notificationSubtitleContent,
      items: [
        messageNotificationListItem,
        phoneNotificationListItem,
        emailNotificationListItem,
        notificationSoundListItem,
        dndNotificationListItem,
      ],
    }),
    [
      dndNotificationListItem,
      emailNotificationListItem,
      messageNotificationListItem,
      notificationSoundListItem,
      notificationSubtitleContent,
      phoneNotificationListItem,
    ]
  );

  const bikeBellListItem = useMemo(
    () => ({
      id: "bikeBell",
      icon: <WfIconMiscBikeBellRegular />,
      content: t`Bike bell`,
      variant: MenuListItemVariant.InternalLink,
      linkLocation: `/device-config/${appToken}/bell`,
      linkComponent: ReactRouterLinkWrapper,
      disabled: !isDeviceConnected,
      hidden: !bikeBellAvailable,
      "data-testid": "settings.device.bikeBell",
    }),
    [appToken, bikeBellAvailable, isDeviceConnected]
  );

  const soundsConfig: ConfigSection = useMemo(
    () => ({
      name: t`Sounds`,
      items: [bikeBellListItem],
    }),
    [bikeBellListItem]
  );

  const deviceAccessConfig: ConfigSection = useMemo(
    () => ({
      items: [usbAccessListItem, usbFitExportListItem, devicePasscodeListItem],
    }),
    [devicePasscodeListItem, usbFitExportListItem, usbAccessListItem]
  );

  const configItems: ConfigSection[] = useMemo(
    () => [
      displayConfig,
      soundsConfig,
      wifiConfig,
      navConfig,
      notificationsConfig,
      sensorConfig,
      deviceAccessConfig,
    ],
    [
      displayConfig,
      navConfig,
      notificationsConfig,
      wifiConfig,
      soundsConfig,
      sensorConfig,
      deviceAccessConfig,
    ]
  );

  const configIsLoading = useMemo(
    () =>
      deviceLoading ||
      autoShutdownIsLoading ||
      displayModeIsLoading ||
      backlightCfgIsLoading ||
      backlightDurationIsLoading ||
      alwaysRotateMapsEnabledIsLoading ||
      autoRerouteEnabledIsLoading ||
      msgAlertsEnabledIsLoading ||
      phoneAlertsEnabledIsLoading ||
      emailAlertsEnabledIsLoading ||
      notificationSoundEnabledIsLoading ||
      dndIntervalSecIsLoading ||
      usbAccessEnabledIsLoading ||
      usbFitExportEnabledIsLoading ||
      devicePasscodeStateIsLoading ||
      autoControlBikeLightsEnabledIsLoading,
    [
      deviceLoading,
      autoShutdownIsLoading,
      displayModeIsLoading,
      backlightCfgIsLoading,
      backlightDurationIsLoading,
      alwaysRotateMapsEnabledIsLoading,
      autoRerouteEnabledIsLoading,
      msgAlertsEnabledIsLoading,
      phoneAlertsEnabledIsLoading,
      emailAlertsEnabledIsLoading,
      notificationSoundEnabledIsLoading,
      dndIntervalSecIsLoading,
      usbAccessEnabledIsLoading,
      usbFitExportEnabledIsLoading,
      devicePasscodeStateIsLoading,
      autoControlBikeLightsEnabledIsLoading,
    ]
  );

  const configError = useMemo(
    () =>
      deviceError ||
      autoShutdownError ||
      displayModeError ||
      backlightCfgError ||
      backlightDurationError ||
      alwaysRotateMapsEnabledError ||
      autoRerouteEnabledError ||
      emailNotificationStatusError ||
      msgNotificationStatusError ||
      phoneNotificationStatusError ||
      notificationSoundEnabledError ||
      dndIntervalSecError ||
      usbAccessEnabledError ||
      usbFitExportEnabledError ||
      devicePasscodeStateError ||
      autoControlBikeLightsEnabledError,
    [
      deviceError,
      autoShutdownError,
      displayModeError,
      backlightCfgError,
      backlightDurationError,
      alwaysRotateMapsEnabledError,
      autoRerouteEnabledError,
      emailNotificationStatusError,
      msgNotificationStatusError,
      phoneNotificationStatusError,
      notificationSoundEnabledError,
      dndIntervalSecError,
      usbAccessEnabledError,
      usbFitExportEnabledError,
      devicePasscodeStateError,
      autoControlBikeLightsEnabledError,
    ]
  );

  const reloadConfig = useCallback(() => {
    reloadDevice();
    mutateAutoShutdownMinutes();
    mutateDisplayMode();
    mutateBacklightCfg();
    mutateBacklightDurationSec();
    mutateAlwaysRotateMapsEnabled();
    mutateAutoRerouteEnabled();
    mutateEmailAlertsEnabled();
    mutateMsgAlertsEnabled();
    mutatePhoneAlertsEnabled();
    mutateNotificationSoundEnabled();
    mutateDndIntervalSec();
    mutateDevicePasscodeState();
    mutateUsbAccessEnabled();
    mutateUsbFitExportEnabled();
    mutateAutoControlBikeLightsEnabled();
  }, [
    mutateAlwaysRotateMapsEnabled,
    mutateAutoControlBikeLightsEnabled,
    mutateAutoRerouteEnabled,
    mutateAutoShutdownMinutes,
    mutateBacklightCfg,
    mutateBacklightDurationSec,
    mutateDevicePasscodeState,
    mutateDisplayMode,
    mutateDndIntervalSec,
    mutateEmailAlertsEnabled,
    mutateMsgAlertsEnabled,
    mutateNotificationSoundEnabled,
    mutateUsbFitExportEnabled,
    mutatePhoneAlertsEnabled,
    mutateUsbAccessEnabled,
    reloadDevice,
  ]);

  return {
    configItems,
    configError,
    configIsLoading,
    reloadConfig,
    device,
    isDeviceConnected: deviceLoading || isDeviceConnected,
    showWorkoutProfilesAlert,
    onCloseWorkoutProfilesAlert,
  };
}

export { useDeviceSettings };
